import React, { useCallback } from 'react'
import Link from 'next/link'
import { CreditCardData, PurchaseType } from '~/types'
import styled from 'styled-components'
import Modal from '~/components/molecules/Modal'
import Button from '~/components/atoms/Button'
import Count from '~/components/atoms/Count'
import Price from '~/components/atoms/Price'
import CreditCardArea from './CreditCardArea'

interface Props {
  type: PurchaseType
  show: boolean
  itemPrice: number
  purchaseNumber: number
  creditCard: CreditCardData
  busy: boolean
  ready: boolean
  onClose(): void
  onPurchase(): Promise<void>
  onRemove(): void
  onChange?(purchaseNumber: number): void
  onOpenCreditCardModal(): void
}

export default function PurchaseModal({
  type,
  show,
  itemPrice,
  purchaseNumber,
  creditCard,
  busy,
  ready,
  onClose,
  onPurchase,
  onRemove,
  onChange,
  onOpenCreditCardModal
}: Props) {
  const handlePurchase = useCallback(async () => {
    // ちょっと冗長だが購入連打してほしくないので念の為入れてる
    if (busy) {
      return
    }
    await onPurchase()
  }, [onPurchase, busy])

  const handleRemoveCard = useCallback(() => {
    onRemove()
  }, [])

  return (
    <Modal show={show} type='slide' onClose={onClose}>
      <Container>
        <Title>{type === 'gift' ? '布教用に購入' : '自分用に購入'}</Title>
        <Settings>
          {type === 'gift' ? (
            <>
              <SettingContainer>
                <SettingHead>購入部数</SettingHead>
                <SettingValue>
                  <Count current={purchaseNumber} onChange={onChange} max={99} />
                </SettingValue>
              </SettingContainer>
              <SettingContainer>
                <SettingHead>
                  <Price price={itemPrice} />×{purchaseNumber}
                </SettingHead>
                <SettingValue>
                  <Price price={itemPrice * purchaseNumber} />
                </SettingValue>
              </SettingContainer>
            </>
          ) : (
            <>
              <SettingContainer>
                <SettingHead>
                  <Price price={itemPrice} />
                  ×1
                </SettingHead>
                <SettingValue>
                  <Price price={itemPrice} />
                </SettingValue>
              </SettingContainer>
            </>
          )}
          <CreditCardArea
            creditCard={creditCard}
            busy={busy}
            onClick={onOpenCreditCardModal}
            onRemove={handleRemoveCard}
          />
          <SettingContainer>
            <SettingHead>支払時期・方法</SettingHead>
            <SettingValue>
              クレジットカード払い
              <br />
              （一括）
            </SettingValue>
          </SettingContainer>
          <SettingContainer>
            <SettingHead>引き渡し・提供時期</SettingHead>
            <SettingValue>購入と同時</SettingValue>
          </SettingContainer>
        </Settings>
        <Buttons>
          <Button
            label='購入する'
            color='primary'
            onClick={handlePurchase}
            disabled={!ready || busy}
          />
          <Button label='キャンセル' color='cancel' onClick={onClose} disabled={busy} />
        </Buttons>
        <NoteContainer>
          <Note>
            購入すると当該作品につき
            <StyledLink href='/terms' passHref>
              利用規約
            </StyledLink>
            に則った利用が可能になります
          </Note>
          <Note>
            返品・返金については
            <StyledLink href='/help' passHref>
              ヘルプ
            </StyledLink>
            をご確認ください
          </Note>
        </NoteContainer>
      </Container>
    </Modal>
  )
}

const Container = styled.div`
  margin: 0 auto;
  text-align: center;
`

const Title = styled.p`
  ${({ theme }) => theme.fonts.Bold20};
  color: ${({ theme }) => theme.colors.OnSurface2};
  margin: 0 0 24px;
`

const Settings = styled.div`
  margin-top: 32px;
  display: grid;
  grid-gap: 24px;
  grid-template-rows: auto auto;
`

const SettingContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SettingHead = styled.div`
  ${({ theme }) => theme.fonts.Bold16};
  color: ${({ theme }) => theme.colors.OnSurface2};
  text-align: left;
`

const SettingValue = styled.div`
  ${({ theme }) => theme.fonts.Bold16};
  color: ${({ theme }) => theme.colors.OnSurface2};
  text-align: right;
`

const Buttons = styled.div`
  margin-top: 24px;
  display: grid;
  grid-gap: 16px;
  grid-template-rows: auto auto;
`
const NoteContainer = styled.div`
  ${({ theme }) => theme.fonts.Bold14};
  color: ${({ theme }) => theme.colors.OnSurface2};
  margin-top: 24px;
  text-align: left;
  display: grid;
  grid-gap: 8px;
  grid-template-rows: auto auto;
`

const Note = styled.p``

const StyledLink = styled(Link)`
  ${({ theme }) => theme.fonts.Bold14};
  color: ${({ theme }) => theme.colors.OnSurface5};
`
